import React, { useCallback, useContext, useMemo, useState } from "react";
import { createClasses, IconButton } from "@bloomberg/media-ui-core";
import { InfoStroke, Lightbulb } from "@bloomberg/media-ui-icons";
import { Popover } from "@bloomberg/media-ui-overlay";
import { ScreenContext } from "@foundation/screen";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import DonutChart from "~components/DonutChart";
import ShareFeedback from "~components/ShareFeedback";
import {
  useSecurityTypeData,
  useCurrentWatchlist,
} from "~context/WatchlistContext";
import { DASHBOARD_v2_FEEDBACK_URL } from "~constants/Portfolio";

const DONUT_CHART_FEEDBACK_URL =
  "https://bmedia.iad1.qualtrics.com/jfe/form/SV_8e7mBA6X3KbDQYm";

// a function which takes the breakpoint as input and returns the
// width and height of the chart as output
function getChartDimensions(
  mobile: boolean | null,
  largeDesktop: boolean | null
) {
  if (largeDesktop)
    return {
      chartSize: 200,
      innerRadius: 70,
      outerRadius: 100,
    };
  else if (mobile)
    return {
      chartSize: 162,
      innerRadius: 56,
      outerRadius: 81,
    };

  return { chartSize: 140, innerRadius: 49, outerRadius: 70 };
}

const makePlural = (str: string, count = 0) => {
  if (count <= 1) {
    return str;
  }

  return `${str}s`;
};

export default function SecurityTypeGrouping() {
  const {
    breakpoints: { smallDesktop, mobile, largeDesktop },
  } = useContext(ScreenContext);

  // V2: Get data from zustand store via selector
  const securityTypes = useSecurityTypeData();
  const currentWatchlist = useCurrentWatchlist();

  const [element, setElement] = useState<Element | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleTooltip = useCallback(() => {
    setShowTooltip((prevShowTooltip) => {
      const eventLabel = !prevShowTooltip
        ? "tooltip_symbol_chart_open"
        : "tooltip_symbol_chart_close";

      trackClickEvent("watchlistToolTip", eventLabel, "click", "engagement");

      return !prevShowTooltip;
    });
  }, []);

  const closeTooltip = useCallback(() => {
    trackClickEvent(
      "watchlistToolTip",
      "tooltip_symbol_chart_close",
      "click",
      "engagement"
    );
    setShowTooltip(false);
  }, []);

  const data = useMemo(
    () =>
      securityTypes.map((item) => ({
        label: item.name,
        color: item.color,
        value: item.percentage,
      })),
    [securityTypes]
  );

  if (!securityTypes.length) {
    return null;
  }

  const { chartSize, innerRadius, outerRadius } = getChartDimensions(
    mobile,
    largeDesktop
  );

  const containerClasses = createClasses([styles.securityTypeGroup, styles.v2]);

  const tippyText = (
    <span>
      See the breakdown of symbol types in your watchlist. Lots (or shares)
      aren’t considered at this time.
      <br />
      <br />
      Have feedback?{" "}
      <ShareFeedback
        feedbackUrl={DASHBOARD_v2_FEEDBACK_URL}
        trackingEventLabel="survey_symbol_chart"
      />
    </span>
  );

  return (
    <div className={containerClasses}>
      <div className={styles.title}>
        Symbol Type{" "}
        <IconButton
          className={styles.info}
          size="small"
          onClick={toggleTooltip}
          ref={setElement}
        >
          <InfoStroke />
        </IconButton>
        {showTooltip && element && (
          <Popover
            Icon={<Lightbulb />}
            allowedPlacements={["top-start", "top-end"]}
            eyebrow="How does this work?"
            text={tippyText}
            anchorElement={element}
            onDismiss={closeTooltip}
            acceptZeroDimensionAnchors
          />
        )}
      </div>
      <div className={styles.investmentData}>
        <div className={styles.donut}>
          <DonutChart
            className={styles.donutChart}
            size={chartSize}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            data={data}
          >
            <text
              className={styles.symbolCount}
              x={outerRadius}
              y={outerRadius - 8}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {currentWatchlist?.positions.length || 0}
            </text>
            <text
              className={styles.symbolCountText}
              x={outerRadius}
              y={outerRadius + 12}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {makePlural("Symbol", currentWatchlist?.positions.length || 0)}
            </text>
          </DonutChart>
        </div>
        <div className={styles.securityTypeLegendsContainer}>
          <div className={styles.securityTypeLegends}>
            {securityTypes.map(
              ({ id, percentage, name, color: backgroundColor }) =>
                percentage > 0 && (
                  <div key={id} className={styles.securityTypeLegend}>
                    <div className={styles.iconTitleContainer}>
                      <div
                        className={styles.legendIcon}
                        style={{ backgroundColor }}
                      ></div>
                      <div className={styles.legendTitle}>{name}</div>
                    </div>
                    <div className={styles.legendValue} data-testid={name}>
                      {Math.round(percentage)}%
                    </div>
                  </div>
                )
            )}
          </div>
        </div>
      </div>
      <ShareFeedback
        className={styles.shareFeedback}
        feedbackUrl={DONUT_CHART_FEEDBACK_URL}
        feedbackText="Share feedback"
        showIcon
        trackingEventLabel="survey_symbol_chart"
      />
    </div>
  );
}
